import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import styled from "@emotion/styled";
import Header from "../../../components/header";
import BlogCover from "../../../components/BlogCover";
import BlogPreview from "../../home/blogPreview";
import CTA from "../../home/cta";
import useBlogStore from "../../../store/blogStore";
import blogPlaceholder from "../../../images/blog_placeholder.png";

const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };
const geist = { fontFamily: "'Geist', sans-serif" };

const DARK = "#132A22";
const CREAM = "#F7F4EE";
const ACCENT = "#295B4A";

/* ---------------- date / read-time helpers ---------------- */
const getOrdinalSuffix = (n) => {
  if (n > 3 && n < 21) return "th";
  switch (n % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
};

const formatBlogDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  const day = date.getDate();
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `Posted ${day}${getOrdinalSuffix(day)} ${months[date.getMonth()]}, ${date.getFullYear()}`;
};

const stripMarkdownToText = (text) => {
  if (!text) return "";
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/[^\s]+/g, "")
    .replace(/[#*<>[\]()!\n]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const estimateReadTime = (content) => {
  if (!content) return "3 min read";
  const words = stripMarkdownToText(content).split(/\s+/).length;
  const mins = Math.max(1, Math.round(words / 200));
  return `${mins} min read`;
};

/* ---------------- markdown processing (preserved) ---------------- */
const extractImageFromMarkdown = (text) => {
  if (!text) return null;
  const imageMatch = text.match(/!\[([^\]]*)\]\(([^)]+)\)/);
  return imageMatch ? imageMatch[2] : null;
};

// Remove the first markdown image so it only appears as the hero (not duplicated in body)
const stripFirstImageFromMarkdown = (text) => {
  if (!text) return "";
  return text.replace(/!\[[^\]]*\]\([^)]+\)\s*\n?/, "").trim();
};

// Parse alt for alignment suffix: "caption|left" or "caption|right" (from admin editor)
const IMAGE_ALT_ALIGN_REGEX = /^(.+?)\s*\|\s*(left|right)\s*$/;
function parseImageAltAndAlign(alt) {
  if (!alt || typeof alt !== "string") return { alt: "", align: "center" };
  const m = alt.trim().match(IMAGE_ALT_ALIGN_REGEX);
  if (m) return { alt: m[1].trim(), align: m[2] };
  return { alt: alt.trim(), align: "center" };
}

const BlogImage = ({ src, alt, ...props }) => {
  const { alt: cleanAlt, align } = parseImageAltAndAlign(alt);
  const alignClass =
    align === "left" ? "blog-img-left" : align === "right" ? "blog-img-right" : "blog-img-center";
  return <img src={src} alt={cleanAlt} className={alignClass} {...props} />;
};

// Keep **...** as one bold region but preserve line breaks inside.
const fixBoldAcrossNewlines = (text) => {
  if (!text || !text.includes("**")) return text;
  let result = "";
  let i = 0;
  let inBold = false;
  while (i < text.length) {
    if (text.slice(i, i + 2) === "**" && (i === 0 || text[i - 1] !== "\\")) {
      result += "**";
      i += 2;
      inBold = !inBold;
      continue;
    }
    if (inBold && (text[i] === "\n" || text.slice(i, i + 2) === "\n\n")) {
      result += "  \n";
      i += text[i] === "\n" ? 1 : 2;
      continue;
    }
    result += text[i];
    i += 1;
  }
  return result;
};

// Normalize markdown so ReactMarkdown produces proper paragraph breaks
const normalizeMarkdownContent = (text) => {
  if (!text) return "";
  text = text.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<\/p>\s*<p[^>]*>/gi, "\n\n");
  text = text.replace(/<p[^>]*>/gi, "");
  text = text.replace(/<\/p>/gi, "\n\n");
  text = text.replace(/<[^>]+>/g, "");
  text = text.replace(/\r\n/g, "\n");
  text = text.replace(/\r/g, "\n");
  text = fixBoldAcrossNewlines(text);
  const LINE_BREAK_PLACEHOLDER = "\x00MD_LB\x00";
  text = text.replace(/ {2}\n/g, LINE_BREAK_PLACEHOLDER);
  text = text.replace(/([^\n])\n([^\n])/g, "$1\n\n$2");
  text = text.split(LINE_BREAK_PLACEHOLDER).join("  \n");
  text = text.replace(/\n{3,}/g, "\n\n");
  return text.trim();
};

const MarkdownContainer = styled.div`
  font-family: "Geist", sans-serif;
  color: #44514b;

  p,
  li {
    font-size: clamp(15px, 1.12vw, 17px);
    line-height: 1.85;
    color: #44514b;
  }

  p {
    margin: 0 0 1.6em;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: "Geist", sans-serif;
    font-weight: 700;
    color: ${DARK};
    line-height: 1.3;
    margin: 1.8em 0 0.7em;
  }

  h2 {
    font-size: clamp(22px, 2vw, 28px);
  }
  h3 {
    font-size: clamp(19px, 1.6vw, 22px);
  }
  h4 {
    font-size: clamp(17px, 1.3vw, 19px);
  }
  h5,
  h6 {
    font-size: clamp(16px, 1.15vw, 18px);
  }

  a {
    color: ${ACCENT};
    text-decoration: underline;
  }

  strong {
    color: ${DARK};
    font-weight: 600;
  }

  ul,
  ol {
    margin: 1.2em 0 1.6em;
    padding-left: 1.4em;
    li {
      margin-bottom: 0.5em;
    }
  }

  blockquote {
    margin: 1.8em 0;
    padding: 0.4em 0 0.4em 1.4em;
    border-left: 3px solid ${ACCENT};
    color: #5e5e5e;
    font-style: italic;
  }

  img {
    max-width: 100%;
    height: auto;
    margin: 2em 0;
    display: block;
    border-radius: 14px;
  }

  .blog-img-left {
    margin-right: auto;
  }
  .blog-img-right {
    margin-left: auto;
  }
  .blog-img-center {
    margin-left: auto;
    margin-right: auto;
  }
`;

/* ---------------- icons ---------------- */
const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="18" cy="5" r="2.4" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="6" cy="12" r="2.4" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="18" cy="19" r="2.4" stroke="currentColor" strokeWidth="1.6" />
    <line x1="8.1" y1="10.9" x2="15.9" y2="6.1" stroke="currentColor" strokeWidth="1.6" />
    <line x1="8.1" y1="13.1" x2="15.9" y2="17.9" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const HeartIcon = ({ filled }) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill={filled ? ACCENT : "none"}>
    <path
      d="M12 20.5l-1.45-1.32C5.4 14.5 2 11.4 2 7.6 2 4.9 4.1 3 6.7 3c1.5 0 2.95.7 3.9 1.8C11.55 3.7 13 3 14.5 3 17.1 3 19.2 4.9 19.2 7.6c0 3.8-3.4 6.9-8.55 11.58L12 20.5z"
      stroke={filled ? ACCENT : "currentColor"}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

/* ---------------- states ---------------- */
const HeaderShell = ({ children }) => (
  <div style={{ background: CREAM, minHeight: "100vh" }}>
    <Header />
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        padding: "120px 24px",
      }}
    >
      {children}
    </div>
  </div>
);

/* ---------------- page ---------------- */
const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentBlog, error, fetchBlogById, clearCurrentBlog } = useBlogStore();
  const [liked, setLiked] = useState(false);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBlogById(id);
    return () => clearCurrentBlog();
  }, [id, fetchBlogById, clearCurrentBlog]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: currentBlog?.blog_title, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 1800);
      }
    } catch (e) {
      /* user dismissed the share sheet — no-op */
    }
  };

  if (error && !currentBlog) {
    return (
      <HeaderShell>
        <p style={{ ...geist, color: "#B23B3B", fontSize: "16px", margin: 0 }}>{error}</p>
        <button
          onClick={() => fetchBlogById(id)}
          style={{ ...geist, padding: "10px 22px", borderRadius: "8px", border: `1px solid ${ACCENT}`, background: "transparent", color: ACCENT, cursor: "pointer", fontWeight: 500 }}
        >
          Try again
        </button>
        <button
          onClick={() => navigate("/blog")}
          style={{ ...geist, border: "none", background: "none", color: "#5E5E5E", cursor: "pointer", textDecoration: "underline" }}
        >
          Back to all articles
        </button>
      </HeaderShell>
    );
  }

  if (!currentBlog) {
    return (
      <HeaderShell>
        <div className="blog-spinner" />
      </HeaderShell>
    );
  }

  const heroImage =
    currentBlog.header_image_url ||
    extractImageFromMarkdown(currentBlog.blog_content) ||
    blogPlaceholder;

  const body = currentBlog.header_image_url
    ? currentBlog.blog_content
    : stripFirstImageFromMarkdown(currentBlog.blog_content);

  const meta = [formatBlogDate(currentBlog.created_at), estimateReadTime(currentBlog.blog_content)]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <style>{`
        @keyframes blogSpin { to { transform: rotate(360deg); } }
        .blog-spinner {
          width: 44px; height: 44px; border-radius: 50%;
          border: 3px solid rgba(41, 91, 74, 0.18);
          border-top-color: ${ACCENT};
          animation: blogSpin 0.8s linear infinite;
        }
        .blog-meta-action {
          display: inline-flex; align-items: center; justify-content: center;
          width: 34px; height: 34px; border-radius: 50%;
          border: 1px solid rgba(19, 42, 34, 0.14);
          background: #FFFFFF; color: #5E5E5E; cursor: pointer;
          transition: color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
        }
        .blog-meta-action:hover { color: ${DARK}; border-color: rgba(19,42,34,0.3); transform: translateY(-1px); }

        @media (max-width: 600px) {
          .blog-post-title { font-size: clamp(30px, 9vw, 40px) !important; }
        }
      `}</style>

      {/* ---------- ARTICLE ---------- */}
      <section
        style={{
          background: CREAM,
          boxSizing: "border-box",
          padding: "0 clamp(20px, 5.56vw, 80px) clamp(56px, 6.5vw, 96px)",
        }}
      >
        <Header />

        <article style={{ maxWidth: "900px", margin: "0 auto", paddingTop: "clamp(36px, 4.4vw, 72px)" }}>
          <h1
            className="blog-post-title"
            style={{
              ...instrumentSerif,
              fontWeight: 400,
              fontSize: "clamp(34px, 4.2vw, 60px)",
              lineHeight: 1.08,
              letterSpacing: "-0.01em",
              color: DARK,
              margin: 0,
            }}
          >
            {currentBlog.blog_title}
          </h1>

          {/* meta row */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", margin: "clamp(16px, 1.7vw, 24px) 0 clamp(28px, 3vw, 40px)" }}>
            <span style={{ ...geist, fontSize: "clamp(12px, 0.97vw, 14px)", color: "#5E5E5E" }}>{meta}</span>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button
                type="button"
                className="blog-meta-action"
                onClick={handleShare}
                aria-label="Share article"
                title={shared ? "Link copied!" : "Share"}
              >
                <ShareIcon />
              </button>
              <button
                type="button"
                className="blog-meta-action"
                onClick={() => setLiked((v) => !v)}
                aria-label={liked ? "Unlike article" : "Like article"}
                title={liked ? "Liked" : "Like"}
              >
                <HeartIcon filled={liked} />
              </button>
              {shared && (
                <span style={{ ...geist, fontSize: "12px", color: ACCENT }}>Link copied</span>
              )}
            </div>
          </div>

          {/* hero image */}
          <BlogCover
            src={heroImage}
            alt={currentBlog.blog_title}
            onError={(e) => { e.target.src = blogPlaceholder; }}
            style={{
              width: "100%",
              aspectRatio: "16 / 9",
              borderRadius: "16px",
            }}
          />

          {/* body */}
          <MarkdownContainer style={{ marginTop: "clamp(28px, 3vw, 44px)" }}>
            <ReactMarkdown components={{ img: BlogImage }}>
              {normalizeMarkdownContent(body)}
            </ReactMarkdown>
          </MarkdownContainer>
        </article>
      </section>

      {/* ---------- LATEST BLOGS + CTA (shared sections) ---------- */}
      <BlogPreview />
      <CTA />
    </>
  );
};

export default BlogPost;
